import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  List,
  Modal,
  OutlinedInput,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CancelIcon from '../../../icons/close.svg';
import BackIcon from '../../../icons/back1.svg';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useMemo } from 'react';
import Tick from '../../../icons/Tick.svg';
import { useDispatch, useSelector } from 'react-redux';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import { CustomDropZone } from '../../onboarding/components/CustomDropZone';
import { CreateWorkOrderDto, PreferredContactMethodEnum, PropertySearchDTO, PropertySearchField, PropertyV2Dto, ResidentV2Dto } from '@inosio/payments-sdk';
import { resetDocumentsState, selectDocumentState, uploadDocuments } from '../../../shared/sagas/document/documentSlice';
import 'react-phone-input-2/lib/material.css';
import SearchInput from '../../shared/SearchInput';
import { getPropertiesWithoutPagination, selectPropertiesState } from '../../properties/sagas/pmPropertiesSlice';
import { getResidentsData, selectResidents } from '../../residents/saga/pmResidentsSlice';
import { createWorkOrder, resetCreateWorkOrderState, selectWorkOrderState } from '../sagas/workorderSlice';
import { PhoneNumberInput } from '../../../shared/PhoneNumberInput';
import { E164PhoneRegex, EmailRegex } from '../../../utils/regex';

const leaseStatusSortPrecedence: Record<string, number> = {
  Future: 1,
  Current: 2,
  Past: 3,
  Other: 4,
};

export interface CreateWorkOrderModalProps {
  open: boolean;
  onClose: () => void;
}

const useStyles = makeStyles((theme) => ({
  globalError: {
    display: 'flex',
    flexDirection: 'row',
    borderTop: '2px  solid #DB3939',
    borderRadius: '24px',
    padding: '12px 20px',
    width: '360px',
    height: '49px',
    justifyContent: 'center',
  },
  termsAndConditionLink: {
    fontFamily: 'Work Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '22px',
    color: '#39A2DB !important',
    marginTop: '18px',
    cursor: 'pointer',
  },
  stepperContainer: {
    marginTop: '90px',
    width: '100%',
  },
  stepper: {
    width: '100%',
    '& .MuiStepLabel-root': {
      display: 'flex',
      flexDirection: 'column-reverse !important',
      marginTop: '-65px',
    },
    '& .MuiStepLabel-label': {
      display: 'flex',
      justifyContent: 'center',
    },
    '& .MuiStepLabel-labelContainer': {
      marginBottom: '9px',
    },
    '& .MuiStepConnector-root': {
      top: '15px',
    },
    '& .MuiStepIcon-root': {
      color: '#F1F0F5',
      width: '29px',
      height: '29px',
    },
    '& .MuiStepIcon-root.Mui-completed': {
      color: '#39DB8D',
    },
    '& .MuiStepIcon-root .MuiStepIcon-text': {
      fill: '#14142B',
      fontWeight: '600',
    },
    '& .MuiStepIcon-root.Mui-active': {
      color: '#39DB8D',
    },
    '& .MuiStepIcon-root.Mui-active .MuiStepIcon-text': {
      fill: '#fff',
    },
  },
  stepperText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '15px !important',
    lineHeight: '21px !important',
    maxWidth: '220px',
    color: '#14142B',
  },
  textStyle: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '16px !important',
    lineHeight: '21px !important',
    color: '#14142B',
  },
  textStyleHelperText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '14px !important',
    lineHeight: '21px !important',
    color: '#14142B',
  },
  textStyleRequired: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '12px !important',
    lineHeight: '21px !important',
    color: 'red',
  },
  textStyleConfirmation: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '18px !important',
    lineHeight: '21px !important',
    color: '#14142B',
  },
  headingStyleConfirmation: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal',
    fontWeight: '700px',
    fontSize: '18px !important',
    lineHeight: '21px !important',
    color: '#14142B',
  },
  contactInput: {
    borderRadius: '24px !important',
    height: '49px  !important',
    outline: 'none !important',
    width: '100%',
  },
  // Applied to <Radio />
  root: {
    width: 19,
    height: 19,
  },
  payerPageText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '400 !important',
    fontSize: '14px !important',
    lineHeight: '22px !important',
    color: '#000000 !important',
  },
}));

function CreateWorkOrderModal(props: CreateWorkOrderModalProps) {
  //------Styles-----//
  const matchesMobile = useMediaQuery('(max-width:600px)');
  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '16px',
    minHeight: matchesMobile ? '100%' : '523px',
    pointerEvents: 'auto',
    width: '98%',
    maxWidth: '472px',
    borderRadius: matchesMobile ? '0px' : '24px',
  };
  const classes = useStyles();

  //-----Redux-----//

  const maxDescriptionCharLength = 5000;
  const maxAccessCodeCharLength = 500;

  const dispatch = useDispatch();
  const { isCreatingWorkOrder, isCreatingWorkOrderSuccess, CreateWorkOrderError, isCreatingWorkOrderFailed, workOrderDetailDto } = useSelector(selectWorkOrderState);
  const { isUploadingDocuments, isUploadingDocumentsFailed, documentErrorMessage } = useSelector(selectDocumentState);

  const [searchValue, setSearchValue] = React.useState('');
  const searchValueChangeHandler = (e: any) => {
    setSearchValue(e.target.value ? e.target.value.trim() : '');
  };
  const [isSearchValueEmpty, setIsSearchValueEmpty] = React.useState(false);
  React.useEffect(() => {
    if (searchValue.length === 0) {
      setIsSearchValueEmpty(true);
    } else {
      setIsSearchValueEmpty(false);
    }
  }, [searchValue]);

  // Select a property flow: start
  const [selectedProperty, setSelectedProperty] = React.useState<PropertyV2Dto | null>(null);
  const [isPropertyError, setIsPropertyError] = React.useState(false);
  const { isFetchingProperties, pmPropertiesWithoutPagination } = useSelector(selectPropertiesState);
  const searchPropertyHandler = () => {
    dispatch({
      type: getPropertiesWithoutPagination.type,
      payload: {
        RangeSize: 1000,
        StartIndex: 0,
        SearchField: PropertySearchField.PropertyName,
        SearchValue: searchValue,
      } as PropertySearchDTO,
    });
  };
  useEffect(() => {
    dispatch({
      type: getPropertiesWithoutPagination.type,
      payload: {
        RangeSize: 1000,
        StartIndex: 0,
      } as PropertySearchDTO,
    });
  }, []);
  // Select a property flow: end

  // Select a resident flow: start
  const [selectedResident, setSelectedResident] = React.useState('');
  const { residentsHistory, isFetchingResidents } = useSelector(selectResidents);

  const sortedResidentHistory = useMemo(() => {
    return {
      ...residentsHistory,
      Rows: [...residentsHistory.Rows].sort(
        (resA: ResidentV2Dto, resB: ResidentV2Dto) => (leaseStatusSortPrecedence[resA.Lease.LeaseStatus] || Infinity) - (leaseStatusSortPrecedence[resB.Lease.LeaseStatus] || Infinity),
      ),
    };
  }, [residentsHistory, leaseStatusSortPrecedence]);

  const searchResidents = () => {
    dispatch({
      type: getResidentsData.type,
      payload: {
        RangeSize: 100,
        StartIndex: 0,
        PropertyUuid: selectedProperty,
      },
    });
  };

  useEffect(() => {
    if (selectedResident) {
      const resident = sortedResidentHistory.Rows.find((row: ResidentV2Dto) => row.Uuid === selectedResident);
      const firstRegisteredUser = resident?.UserProfiles?.[0];
      setContactEmail(firstRegisteredUser?.UserEmail ?? resident?.IntegrationEmail ?? '');
      setContactPhone(firstRegisteredUser?.UserPhoneNumber ?? resident?.IntegrationPhone ?? '');
      setContactName(
        firstRegisteredUser ? `${firstRegisteredUser?.FirstName ?? ''} ${firstRegisteredUser?.LastName ?? ''}` : resident ? `${resident?.FirstName ?? ''} ${resident?.LastName ?? ''}` : '',
      );
    }
  }, [selectedResident]);

  // Select a property flow: end

  //----all input form fields and their errors-----//

  const [contactName, setContactName] = React.useState('');
  const [contactPhone, setContactPhone] = React.useState('');
  const [contactEmail, setContactEmail] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [petNotes, setPetNotes] = React.useState('');
  const [accessNotes, setAccessNotes] = React.useState('');
  const [descriptionLengthLeft, setDescriptionLengthLeft] = React.useState(maxDescriptionCharLength);
  const [accessNotesLengthLeft, setAccessNotesLengthLeft] = React.useState(maxAccessCodeCharLength);

  //errors
  const [petNotesError, setPetNotesError] = React.useState(false);
  const [accessNotesError, setAccessNotesError] = React.useState(false);
  const [contactNameError, setContactNameError] = React.useState(false);
  const [contactPhoneError, setContactPhoneError] = React.useState(false);
  const [contactEmailError, setContactEmailError] = React.useState(false);
  const [titleError, setTitleError] = React.useState(false);
  const [descriptionError, setDescriptionError] = React.useState(false);

  //validations
  const isValidEmail = (email: string) => {
    return EmailRegex.test(email);
  };

  const isValidPhoneNumber = (number: string) => {
    return E164PhoneRegex.test(number);
  };

  //------attachments section-----//

  const [workOrderFiles, setWorkOrderFiles] = React.useState<Array<any>>([]);
  const handleWorkOrderFiles = (acceptedFiles: File[]) => {
    let files = workOrderFiles;

    if (files.length + acceptedFiles.length <= 5) {
      acceptedFiles.forEach((af: File) => {
        if (!files.find((f) => f.name === af.name)) {
          files = [...files, af];
        }
      });
      setWorkOrderFiles(files);
    }
  };

  const handleDeleteworkOrderFiles = (file: File) => {
    setWorkOrderFiles(workOrderFiles.filter((f: File) => f.name !== file.name));
  };

  //-----stepper section------//
  const [activeStep, setActiveStep] = React.useState(0);
  const steps = ['Choose a Property', 'Select Tenant', 'Describe Issue', 'Contact Details', 'Access Details', 'Attachments'];
  const handleBack = () => {
    if (activeStep === 1) setSelectedResident('');

    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const submitCreateWorkOrder = () => {
    dispatch({
      type: createWorkOrder.type,
      payload: {
        Title: title,
        Description: description,
        ContactName: contactName,
        ContactEmail: contactEmail,
        ContactPhone: contactPhone,
        AccessNotes: getAccessNotes(),
        PetNotes: anyAnimalsValue === 'Yes' ? petNotes : '',
        PreferredContactMethod: preferredContactValue,
        propertyUUID: selectedProperty?.Uuid,
        ResidentUuid: selectedResident ? selectedResident : undefined,
      } as CreateWorkOrderDto,
    });
  };

  const handleNext = () => {
    if (activeStep < 6) {
      const newActiveStep = activeStep + 1;
      switch (activeStep) {
        case 0:
          if (selectedProperty) {
            setActiveStep(newActiveStep);
            setIsPropertyError(false);
            setSearchValue('');
            searchResidents();
          } else {
            setIsPropertyError(true);
          }
          break;
        case 1:
          setActiveStep(newActiveStep);
          setSearchValue('');
          break;
        case 2:
          if (!title) setTitleError(true);
          if (!description) setDescriptionError(true);
          if (title && description) {
            setTitleError(false);
            setDescriptionError(false);
            setActiveStep(newActiveStep);
          }
          break;
        case 3:
          setContactNameError(false);
          setContactPhoneError(false);
          setContactEmailError(false);
          if (!contactName) setContactNameError(true);
          if (!isValidPhoneNumber(contactPhone)) setContactPhoneError(true);
          if (!isValidEmail(contactEmail)) setContactEmailError(true);
          if (isValidEmail(contactEmail) && contactName && isValidPhoneNumber(contactPhone)) {
            setActiveStep(newActiveStep);
          }
          break;
        case 4:
          //Note: blank means no option selected yet. So need to throw an error or don't allow to proceed

          if (alarmValue === '') break;

          if (anyAnimalsValue === '') break;
          if (anyAnimalsValue === 'Yes' && !petNotes) {
            setPetNotesError(true);
            break;
          }

          if (timesToAvoidValue === '') break;
          if (timesToAvoidValue === 'Yes' && accessNotesLengthLeft < 0) {
            setAccessNotesError(true);
            break;
          }

          setPetNotesError(false);
          setAccessNotesError(false);
          setActiveStep(newActiveStep);
          break;
        case 5:
          submitCreateWorkOrder();
          break;
        default:
          break;
      }
    }
  };

  const getAccessNotes = () => {
    let returnValue = '';
    if (alarmValue === 'Yes') returnValue = 'ALARM SET - CONTACT RESIDENT FOR INSTRUCTIONS';
    if (timesToAvoidValue === 'Yes') returnValue += `${alarmValue === 'Yes' ? '<br/>' : ''}Avoid times: ${accessNotes}`;
    return returnValue;
  };

  //reset all the states
  const handleClose = () => {
    setActiveStep(0);
    setSearchValue('');
    setIsSearchValueEmpty(false);
    setSelectedProperty(null);
    setIsPropertyError(false);
    setSelectedResident('');
    setPetNotes('');
    setAccessNotes('');
    setContactEmail('');
    setContactName('');
    setContactPhone('');
    setTitle('');
    setDescription('');
    setWorkOrderFiles([]);
    setContactNameError(false);
    setContactEmailError(false);
    setTitleError(false);
    setDescriptionError(false);
    setContactPhoneError(false);
    setAlarmValue('');
    setAnyAnimalsValue('');
    setTimesToAvoidValue('');
    setAccessNotesError(false);
    setPetNotesError(false);
    dispatch({
      type: resetCreateWorkOrderState.type,
    });
    dispatch({
      type: resetDocumentsState.type,
    });
  };

  //-----radio buttons section----//

  //preferred contact method
  const [preferredContactValue, setPreferredContactValue] = React.useState<PreferredContactMethodEnum>(PreferredContactMethodEnum.Call);

  const handlePreferredContactChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPreferredContactValue((event.target as HTMLInputElement).value as PreferredContactMethodEnum);
  };

  //An Alarm
  const [alarmValue, setAlarmValue] = React.useState('');

  const handleAlarmValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAlarmValue((event.target as HTMLInputElement).value);
  };

  //Any animals
  const [anyAnimalsValue, setAnyAnimalsValue] = React.useState('');

  const handleAnyAnimalsValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAnyAnimalsValue((event.target as HTMLInputElement).value);
  };

  //Times to avoid
  const [timesToAvoidValue, setTimesToAvoidValue] = React.useState('');

  const handleTimesToAvoidValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTimesToAvoidValue((event.target as HTMLInputElement).value);
  };

  //---useEffects---//

  useEffect(() => {
    if (isCreatingWorkOrderSuccess) {
      const uploadingFiles = workOrderFiles?.filter((f) => f.name);

      if (uploadingFiles.length > 0) {
        dispatch({
          type: uploadDocuments.type,
          payload: {
            belongsToEntityUuid: workOrderDetailDto?.Uuid,
            documents: uploadingFiles,
            documentType: 'WorkOrderFile',
          },
        });
      }
    }
  }, [dispatch, isCreatingWorkOrderSuccess]);

  useEffect(() => {
    setDescriptionLengthLeft(maxDescriptionCharLength - description.length);
  }, [description]);

  useEffect(() => {
    setAccessNotesLengthLeft(maxAccessCodeCharLength - accessNotes.length);
  }, [accessNotes]);

  const [snackBarOpen, setSnackBarOpen] = React.useState(false);

  return (
    <>
      <Modal
        open={props.open}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        BackdropProps={{
          invisible: false,
        }}
        disableEscapeKeyDown
      >
        <Card sx={style}>
          {/* Spinner */}
          {isCreatingWorkOrder || isUploadingDocuments ? (
            <Box style={{ width: '100%', height: '100%', position: 'absolute', top: 0, right: 0 }}>
              <Stack direction="column" justifyContent="center" alignItems="center" style={{ height: '100%' }}>
                <SpinnerWithNoConfirmation />
              </Stack>
            </Box>
          ) : //confirmation text
          isCreatingWorkOrderSuccess ? (
            <Grid item sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Grid item pl={4} pr={4}>
                <Typography textAlign={'center'} style={{ fontWeight: 600 }} className={classes.headingStyleConfirmation}>
                  {'Work Order Created'}
                </Typography>
              </Grid>
              <Grid item mt={10}>
                <img src={Tick} alt="tick" />
              </Grid>
              <Grid item pl={4} pr={4} mt={2}>
                <Typography textAlign={'center'} className={classes.textStyleConfirmation}>
                  {`Work Order(${workOrderDetailDto.WorkOrderId}) has been created`}
                </Typography>
              </Grid>
              <Grid item pl={4} pr={4} mt={2}>
                <Typography textAlign={'center'} className={classes.textStyleConfirmation}>
                  {isUploadingDocumentsFailed && `However there was an error uploading the attachments: ${documentErrorMessage?.message}`}
                </Typography>
              </Grid>
              {/* done button */}
              <Box sx={{ position: 'absolute', bottom: matchesMobile ? 40 : 15, width: '94%' }}>
                <Box sx={{ display: 'flex', justifyContent: matchesMobile ? 'center' : 'flex-end' }}>
                  <Button
                    variant="contained"
                    style={{
                      textTransform: 'none',
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: 'normal',
                      fontWeight: 700,
                      backgroundColor: '#39A2DB',
                      borderRadius: '24px',
                      width: matchesMobile ? '342px' : '115px',
                    }}
                    onClick={() => {
                      props.onClose();
                      handleClose();
                    }}
                  >
                    Done
                  </Button>
                </Box>
              </Box>
            </Grid>
          ) : (
            <>
              {/* Error component */}
              {isCreatingWorkOrderFailed || isUploadingDocumentsFailed ? (
                <Grid item sx={{ display: 'flex', justifyContent: 'center' }}>
                  <div className={classes.globalError}>
                    <Typography
                      sx={{
                        fontFamily: "'Open Sans', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 500,
                        fontSize: '14px',
                        lineHeight: '18px',
                        color: '#DB3939',
                      }}
                    >
                      {isUploadingDocumentsFailed ? `Error uploading attachments: ${documentErrorMessage?.message}` : CreateWorkOrderError?.message}
                    </Typography>
                  </div>
                </Grid>
              ) : (
                <Grid item sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Typography id="subTitle" className={classes.stepperText}>
                    {steps[activeStep]}
                  </Typography>
                </Grid>
              )}

              {/* Back Icon */}
              {activeStep === 0 ? (
                <></>
              ) : (
                <Grid item sx={{ position: 'absolute', top: 18, left: 20, display: activeStep === 0 ? 'none' : '' }}>
                  <img src={BackIcon} alt="close" style={{ cursor: 'pointer' }} onClick={handleBack} />
                </Grid>
              )}

              {/* Stepper with labels */}
              {[2, 3, 4, 5].includes(activeStep) && (
                <Box className={classes.stepperContainer} sx={{ display: 'block' }}>
                  <Stepper nonLinear alternativeLabel activeStep={activeStep - 2} classes={{ root: classes.stepper }}>
                    {steps
                      .filter((e, i) => i > 1)
                      .map((label, index) => (
                        <Step key={label}>
                          <StepLabel sx={{ display: 'flex', justifyContent: 'center', cursor: 'pointer' }}>
                            <Typography className={classes.stepperText} sx={{ marginBottom: index === 3 ? '10px' : '0px', marginTop: index === 3 ? '10.5px' : '0px' }}>
                              {label}
                            </Typography>
                          </StepLabel>
                        </Step>
                      ))}
                  </Stepper>
                </Box>
              )}

              <Grid container sx={{ marginTop: '10px', marginBottom: '60px' }}>
                {/* On stepper 0, show title and description inputs*/}
                {activeStep === 0 && (
                  <Grid item mt={2} rowSpacing={2} sx={{ width: '100%', height: '100%' }}>
                    <Grid item xs={12} display="flex" justifyContent="center" width="100%" mb={1}>
                      <SearchInput
                        searchValue={searchValue}
                        searchValueChangeHandler={searchValueChangeHandler}
                        handleSearch={searchPropertyHandler}
                        isSearchValueEmpty={isSearchValueEmpty}
                        placeholder="Search by property name"
                        id="searchProperty"
                        width={matchesMobile ? '300px' : '450px'}
                        height="45px"
                      />
                    </Grid>
                    {isFetchingProperties ? (
                      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }} mt={2}>
                        <CircularProgress size={40} />
                      </Grid>
                    ) : (
                      <Grid
                        item
                        xs={12}
                        mx={2}
                        mb={2}
                        sx={{
                          maxHeight: '355px',
                          overflow: 'auto',
                          '&::-webkit-scrollbar': {
                            width: 7,
                          },
                          '&::-webkit-scrollbar-track': {
                            boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                            borderRadius: '24px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'darkgrey',
                            borderRadius: '24px',
                          },
                        }}
                      >
                        <List>
                          {pmPropertiesWithoutPagination.map((prop) => {
                            return (
                              <Grid item xs={12} p={1} key={prop.Uuid}>
                                <Button
                                  variant="contained"
                                  style={{
                                    width: '100%',
                                    height: '46px',
                                    borderRadius: '24px',
                                    textTransform: 'capitalize',
                                    color: 'black',
                                    backgroundColor: prop.Uuid === selectedProperty?.Uuid ? '#A2DBFA' : '#F1F0F5',
                                    borderColor: prop.Uuid === selectedProperty?.Uuid ? '#A2DBFA' : '#F1F0F5',
                                    fontFamily: 'Work Sans',
                                    fontWeight: 400,
                                    fontSize: '16px',
                                    lineHeight: '22px',
                                    boxShadow: 'none',
                                  }}
                                  onClick={() => setSelectedProperty(prop)}
                                  data-e2e="propertyBtn"
                                >
                                  {`${prop.Name}`}
                                </Button>
                              </Grid>
                            );
                          })}
                        </List>
                      </Grid>
                    )}

                    {isPropertyError && (
                      <Grid item xs={12}>
                        <Typography className={classes.textStyleRequired} style={{ textAlign: 'center' }}>
                          Please choose a property
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                )}
                {/* On stepper 1, show title and description inputs*/}
                {activeStep === 1 && (
                  <Grid item mt={2} rowSpacing={2} sx={{ width: '100%', height: '100%' }}>
                    {isFetchingResidents ? (
                      <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }} mt={2}>
                        <CircularProgress size={40} />
                      </Grid>
                    ) : (
                      <Grid
                        item
                        xs={12}
                        mx={2}
                        mb={2}
                        sx={{
                          maxHeight: '355px',
                          overflow: 'auto',
                          '&::-webkit-scrollbar': {
                            width: 7,
                          },
                          '&::-webkit-scrollbar-track': {
                            boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                            borderRadius: '24px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'darkgrey',
                            borderRadius: '24px',
                          },
                        }}
                      >
                        <List>
                          {sortedResidentHistory.Rows.map((res) => {
                            return (
                              <Grid item xs={12} p={1} key={res.Uuid}>
                                <div
                                  style={{
                                    width: '100%',
                                    borderRadius: '20px',
                                    backgroundColor: res.Uuid === selectedResident ? '#A2DBFA' : '#FFFFFF',
                                    borderColor: res.Uuid === selectedResident ? '#A2DBFA' : '#F1F0F5',
                                    borderWidth: '2px',
                                    borderStyle: 'solid',
                                    padding: '5px 15px',
                                  }}
                                  onClick={() => setSelectedResident(res.Uuid)}
                                  data-e2e="residentBtn"
                                >
                                  <Typography
                                    sx={{
                                      color: 'black',
                                      fontFamily: 'Work Sans',
                                      fontWeight: 400,
                                      fontSize: '16px',
                                      lineHeight: '22px',
                                    }}
                                  >
                                    {`${res.FirstName} ${res.LastName}`}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontWeight: 400,
                                      fontSize: '13px',
                                      color: '#616161',
                                    }}
                                  >
                                    {res.Lease.LeaseStatus}
                                  </Typography>
                                </div>
                              </Grid>
                            );
                          })}
                        </List>
                      </Grid>
                    )}
                  </Grid>
                )}
                {/* On stepper 2, show title and description inputs*/}
                {activeStep === 2 && (
                  <Grid item mt={2} rowSpacing={2} sx={{ width: '100%', height: '100%' }}>
                    <Grid
                      item
                      mb={1}
                      sx={{
                        '& fieldset': {
                          borderRadius: '24px',
                          borderColor: 'white',
                        },
                      }}
                    >
                      <TextField
                        value={title}
                        onChange={(e) => {
                          setTitle(e.target.value);
                        }}
                        sx={{ width: '100%', backgroundColor: '#F1F0F5', borderRadius: '24px' }}
                        placeholder={'Title'}
                        error={titleError}
                        inputProps={{ maxLength: 100 }}
                      />
                    </Grid>
                    <Grid
                      item
                      sx={{
                        '& fieldset': {
                          borderRadius: '24px',
                          borderColor: 'white',
                        },
                      }}
                    >
                      <TextField
                        multiline
                        value={description}
                        onChange={(e) => {
                          setDescription(e.target.value);
                        }}
                        rows={8}
                        sx={{ width: '100%', backgroundColor: '#F1F0F5', borderRadius: '24px' }}
                        placeholder={'Description'}
                        error={descriptionError}
                        inputProps={{ maxLength: maxDescriptionCharLength }}
                      />
                      <Typography
                        className={classes.payerPageText}
                        sx={{ textAlign: 'right', color: descriptionLengthLeft < 0 ? '#DB3939 !important' : '#14142B !important' }}
                      >{`${descriptionLengthLeft} characters left.`}</Typography>
                    </Grid>
                  </Grid>
                )}
                {/* On stepper 3, show contact details inputs */}
                {activeStep === 3 && (
                  <Grid item xs={12} mt={2}>
                    <Grid container rowSpacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth error={contactNameError}>
                          <InputLabel>{'Contact Name'}</InputLabel>
                          <OutlinedInput className={classes.contactInput} label={'Contact Name'} onChange={(e) => setContactName(e.target.value)} value={contactName} />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <PhoneNumberInput value={contactPhone} showErrorMessage={contactPhoneError} onChange={(phone) => setContactPhone(phone.startsWith('+') ? phone : `+${phone}`)} />
                      </Grid>
                      <Grid item xs={12} sm={3} pl={3}>
                        <RadioGroup value={preferredContactValue} onChange={handlePreferredContactChange}>
                          <FormControlLabel
                            sx={{ '& .MuiFormControlLabel-label': { marginLeft: '7px', fontSize: '15px' } }}
                            value={PreferredContactMethodEnum.Call}
                            control={<Radio size="small" classes={{ root: classes.root }} />}
                            label="Call Me"
                          />
                          <FormControlLabel
                            sx={{ '& .MuiFormControlLabel-label': { marginLeft: '7px', fontSize: '15px' } }}
                            value={PreferredContactMethodEnum.Text}
                            control={<Radio size="small" classes={{ root: classes.root }} />}
                            label="Text Me"
                          />
                        </RadioGroup>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <FormControl fullWidth error={contactEmailError}>
                          <InputLabel>{'Email'}</InputLabel>
                          <OutlinedInput className={classes.contactInput} label={'Email'} onChange={(e) => setContactEmail(e.target.value)} value={contactEmail} />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={3} pl={3} display="flex" alignItems="center">
                        <RadioGroup value={preferredContactValue} onChange={handlePreferredContactChange}>
                          <FormControlLabel
                            sx={{ '& .MuiFormControlLabel-label': { marginLeft: '7px', fontSize: '15px' } }}
                            value={PreferredContactMethodEnum.Email}
                            control={<Radio size="small" classes={{ root: classes.root }} />}
                            label="Email Me"
                          />
                        </RadioGroup>
                      </Grid>
                      {contactEmailError ? (
                        <FormHelperText sx={{ marginTop: '5px', marginLeft: '15px' }} error>
                          Invalid Email
                        </FormHelperText>
                      ) : null}
                    </Grid>
                  </Grid>
                )}

                {/* On stepper 4, show access details inputs */}
                {activeStep === 4 && (
                  <Grid item xs={12} mt={2}>
                    <Grid container rowSpacing={2}>
                      {(!alarmValue || !anyAnimalsValue || !timesToAvoidValue) && (
                        <Grid item xs={12}>
                          <Typography className={classes.textStyleRequired}>Required*</Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={8} mt={1}>
                        <Typography className={classes.textStyle}>Is there an alarm?</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4} display="flex" justifyContent={matchesMobile ? 'flex-start' : 'flex-end'}>
                        <RadioGroup value={alarmValue} row onChange={handleAlarmValueChange}>
                          <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                        </RadioGroup>
                      </Grid>
                      <Grid item xs={12} sm={8} mt={1}>
                        <Typography className={classes.textStyle}>Are there any animals in the unit?</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4} display="flex" justifyContent={matchesMobile ? 'flex-start' : 'flex-end'}>
                        <RadioGroup value={anyAnimalsValue} row onChange={handleAnyAnimalsValueChange}>
                          <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                        </RadioGroup>
                      </Grid>
                      {anyAnimalsValue === 'Yes' && (
                        <Grid item xs={12}>
                          <FormControl fullWidth error={petNotesError}>
                            <InputLabel>{'What type ?'}</InputLabel>
                            <OutlinedInput className={classes.contactInput} label={'What type ?'} onChange={(e) => setPetNotes(e.target.value)} value={petNotes} />
                          </FormControl>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={8} mt={1}>
                        <Typography className={classes.textStyle}>Can Maintenance come Mon-Fri 9-5pm?</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4} display="flex" justifyContent={matchesMobile ? 'flex-start' : 'flex-end'}>
                        <RadioGroup value={timesToAvoidValue} row onChange={handleTimesToAvoidValueChange}>
                          <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                        </RadioGroup>
                      </Grid>
                      {timesToAvoidValue === 'Yes' && (
                        <>
                          <Grid item xs={12}>
                            <Typography className={classes.textStyle}>Are there any times to avoid?</Typography>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            sx={{
                              '& fieldset': {
                                borderRadius: '24px',
                                borderColor: 'white',
                              },
                            }}
                          >
                            <TextField
                              multiline
                              rows={2}
                              value={accessNotes}
                              onChange={(e) => {
                                setAccessNotes(e.target.value);
                              }}
                              sx={{ width: '100%', backgroundColor: '#F1F0F5', borderRadius: '24px' }}
                              error={accessNotesError}
                              inputProps={{ maxLength: maxAccessCodeCharLength }}
                            />
                            <Typography
                              className={classes.payerPageText}
                              sx={{ textAlign: 'right', color: accessNotesLengthLeft < 0 ? '#DB3939 !important' : '#14142B !important' }}
                            >{`${accessNotesLengthLeft} characters left.`}</Typography>
                          </Grid>
                        </>
                      )}
                      {timesToAvoidValue === 'No' && (
                        <Grid item xs={12}>
                          <Typography className={classes.textStyleHelperText}>Maintenenace will need to contact you to schedule service. Please note this may result in slower service.</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}

                {/* On stepper 5, show attachment inputs */}
                {activeStep === 5 && (
                  <Grid item sx={{ width: '100%', height: '100%' }}>
                    <Box sx={{ marginBottom: '24px' }}>
                      {/* maxFileSize must be in MB, example: maxFileSize={20} */}
                      <CustomDropZone
                        files={workOrderFiles}
                        fileLimit={5}
                        handleFilesAdded={handleWorkOrderFiles}
                        handleFileDeleted={handleDeleteworkOrderFiles}
                        maxFileSize={50}
                        description="Up to 50MB per file. Up to 5 Files at a time."
                      />
                      <Typography style={{ fontFamily: 'Work Sans', fontSize: '14px', marginTop: '10px' }}>{'Supported File Types: .jpg, .pdf , .png, .mp4, .mov'}</Typography>
                      <Typography variant="body2" gutterBottom id="terms-and-condition" marginTop="10px">
                        By clicking submit you agree to our{' '}
                        <span
                          className={classes.termsAndConditionLink}
                          onClick={() => {
                            window.open('/terms');
                          }}
                        >
                          {'T&Cs...'}
                        </span>
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
              {/* Continue/Submit button */}
              <Box sx={{ position: 'absolute', bottom: matchesMobile ? 40 : 15, width: '94%' }}>
                <Box sx={{ display: 'flex', justifyContent: matchesMobile ? 'center' : 'flex-end' }}>
                  <Button
                    variant="contained"
                    style={{
                      textTransform: 'none',
                      fontFamily: "'Open Sans', sans-serif",
                      fontStyle: 'normal',
                      fontWeight: 700,
                      backgroundColor: '#39A2DB',
                      borderRadius: '24px',
                      width: matchesMobile ? '342px' : '115px',
                    }}
                    onClick={handleNext}
                  >
                    {'Continue'}
                  </Button>
                </Box>
              </Box>
            </>
          )}
          {/* Close Icon */}
          <Grid item sx={{ position: 'absolute', top: 18, right: 20, display: isCreatingWorkOrder || isUploadingDocuments ? 'none' : '' }}>
            <img
              src={CancelIcon}
              alt="close"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                handleClose();
                props.onClose();
              }}
            />
          </Grid>
        </Card>
      </Modal>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        open={snackBarOpen}
        autoHideDuration={4000}
        onClose={() => {
          setSnackBarOpen(false);
        }}
      >
        <Alert
          onClose={() => {
            setSnackBarOpen(false);
          }}
          severity="success"
          sx={{ width: '100%' }}
          id="message-copied"
        >
          Copied to Clipboard
        </Alert>
      </Snackbar>
    </>
  );
}

export default CreateWorkOrderModal;
